import React, { useState } from "react";
import toast from "react-hot-toast";
import { Form, Upload } from "antd";
import { PaperClipOutlined, InboxOutlined } from "@ant-design/icons";
import LoadableButton from "../../../components/button/LoadableButton";
import PDFSignatureSelector from "../../../components/pdfSignature/PDFSignatureSelector";
import { SendToSignApi } from "../../../api/request/service";

const SendSignature = ({ setType, file }) => {
  const { Dragger } = Upload;
  const [form] = Form.useForm();
  const [fileName, setFileName] = useState("");
  const [isUploadFileLoading, setIsUploadFileLoading] = useState(false);
  const [isFileError, setIsFileError] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [signaturePositions, setSignaturePositions] = useState([]);

  const clear = () => {
    setFileName("");
    setPdfFile(null);
    setPdfUrl(null);
    setSignaturePositions([]);
    form.resetFields();
  };

  const beforeUpload = (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      clear();
      setIsFileError(true);
      return true;
    } else {
      setFileName(file.name);
      setIsFileError(false);
      return false;
    }
  };

  const handleChange = ({ fileList }) => {
    if (!fileList?.length) return;

    const file = fileList[0];
    if (file && file.type === "application/pdf") {
      setPdfFile(file?.originFileObj);
      const fileURL = URL.createObjectURL(file?.originFileObj);
      setPdfUrl(fileURL);
      setSignaturePositions([]);
    }
  };

  const sendToSign = async () => {
    if (!pdfFile || !signaturePositions?.length) return;

    try {
      setIsUploadFileLoading(true);
      const params = {
        file_id: file?.id,
        pipeline_tasks_id: file?.taskId,
        client_id: file?.clientId,
        file: pdfFile,
        positions: signaturePositions,
      };
      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await SendToSignApi(params, apiHeader);
      if (data?.status) {
        toast.success(data?.message);
        clear();
        setType("review");
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsUploadFileLoading(false);
    }
  };

  return (
    <Form
      className="w-full"
      autoComplete="off"
      form={form}
      initialValues={{ files: [] }}
    >
      <div>
        <Form.Item
          name="files"
          className="mb-0"
          rules={[{ required: true, message: "Please upload file." }]}
        >
          <Dragger
            beforeUpload={beforeUpload}
            maxCount={1}
            onChange={handleChange}
            showUploadList={false}
            accept="application/pdf"
            multiple={false}
          >
            <div className="flex flex-col items-center justify-center gap-5 p-2">
              <img
                src="/assets/icons/file-upload.svg"
                alt="File Upload"
                className="size-10"
              />
              <div className="space-y-2">
                <p className="font-medium text-lg">
                  Click or drag PDF file to this area to upload
                </p>
                {fileName && (
                  <p>
                    <PaperClipOutlined className="text-Gray" /> {fileName}
                  </p>
                )}
                {isFileError ? (
                  <p className="text-red-500">
                    Only PDF file is allowed. Please upload a valid PDF
                    document.
                  </p>
                ) : null}
              </div>
            </div>
          </Dragger>
        </Form.Item>
      </div>

      <div className="flex flex-col items-center justify-center mt-5">
        {pdfFile && (
          <>
            <div className="max-w-fit overflow-auto">
              <PDFSignatureSelector
                pdfFile={pdfFile}
                pdfUrl={pdfUrl}
                onSave={(data) => {
                  setSignaturePositions(data?.signaturePositions);
                }}
              />
            </div>
            <div className="flex justify-end items-center gap-5">
              <button
                className="btn-outlined"
                onClick={() => setType("review")}
              >
                Cancel
              </button>

              <LoadableButton
                type="submit"
                lable="Upload"
                isLoading={isUploadFileLoading}
                loadingLable="Uploading file..."
                onClick={sendToSign}
                disabled={!pdfFile || !signaturePositions?.length}
              />
            </div>
          </>
        )}
      </div>
    </Form>
  );
};

export default SendSignature;
